import React, { useCallback, useEffect, useState } from 'react'
import { FaExternalLinkAlt, FaLock, FaSync, FaWhatsapp } from 'react-icons/fa'
import whatsappApi from './whatsappApi'

const WA = '#25D366'
const WA_DARK = '#128C7E'

// Created By Cursor — classify Meta Graph billing errors for friendly UI
function classifyBillingError(raw) {
  const msg = String(raw || '').trim()
  const lower = msg.toLowerCase()
  if (
    lower.includes('permission') ||
    lower.includes('(#200)') ||
    lower.includes('does not have permission') ||
    lower.includes('oauth') ||
    lower.includes('access token')
  ) {
    return {
      kind: 'permission',
      title: 'Billing access limited',
      detail:
        'This WhatsApp account token cannot read Meta pricing analytics. An admin must grant analytics / billing access in Meta Business Suite, or open billing there directly.',
    }
  }
  if (lower.includes('credit') || lower.includes('partner')) {
    return {
      kind: 'credit',
      title: 'Partner credit line',
      detail:
        'Meta may hide costs when the WABA uses a partner credit line. View invoices and payment activity in Meta Business Suite.',
    }
  }
  if (msg) {
    return {
      kind: 'other',
      title: 'Could not load Meta charges',
      detail: msg.length > 160 ? `${msg.slice(0, 157)}…` : msg,
    }
  }
  return null
}

// Created By Cursor — Meta WABA cost strip (pricing_analytics). Pay only in Meta Suite.
export default function WhatsAppMetaBillingStrip({ connected = false }) {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    if (!connected) {
      setData(null)
      setError('')
      return
    }
    setLoading(true)
    setError('')
    try {
      const res = await whatsappApi.getMetaBilling()
      if (res.api_status === 'OK' && res.data) {
        setData(res.data)
        if (!res.data.success && res.data.error) {
          setError(res.data.error)
        }
      } else {
        setError(res.api_remark || 'Unable to load Meta billing')
        setData(res.data || null)
      }
    } catch (e) {
      setError(e?.message || 'Unable to load Meta billing')
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [connected])

  useEffect(() => {
    load()
  }, [load])

  const billingUrl =
    data?.billing_manage_url ||
    'https://business.facebook.com/billing_hub/payment_activity'
  const helpUrl =
    data?.payment_help_url ||
    'https://www.facebook.com/business/help/2225184664363779'
  const period =
    data?.period?.from && data?.period?.to
      ? `${data.period.from} → ${data.period.to}`
      : 'This month'
  const errInfo = classifyBillingError(error || data?.hint)
  const costAvailable = !!data?.cost_available && data?.cost_total != null
  const currency = data?.currency || 'INR'
  const hasVolume = data?.volume_total != null && Number(data.volume_total) > 0
  const showPermissionState = !loading && !!errInfo && errInfo.kind === 'permission'

  if (!connected) {
    return (
      <div style={wrapStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <IconBadge>
            <FaWhatsapp size={14} />
          </IconBadge>
          <div>
            <div style={titleStyle}>Meta billing</div>
            <div style={subStyle}>
              Connect WhatsApp to view delivery charges for this period.
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={wrapStyle}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 14,
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 8,
              marginBottom: 12,
            }}
          >
            <IconBadge>
              <FaWhatsapp size={14} />
            </IconBadge>
            <span style={titleStyle}>Meta billing</span>
            <span style={periodPill}>{period}</span>
          </div>

          {loading ? (
            <div style={{ ...subStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
              <FaSync style={{ animation: 'waSpin 0.9s linear infinite' }} />
              Loading Meta charges…
            </div>
          ) : showPermissionState ? (
            <PermissionCard
              title={errInfo.title}
              detail={errInfo.detail}
              billingUrl={billingUrl}
              helpUrl={helpUrl}
            />
          ) : (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                  gap: 10,
                  maxWidth: 420,
                }}
              >
                <StatTile
                  label="Approx. Meta cost"
                  value={
                    costAvailable ? formatMoney(data.cost_total, currency) : '—'
                  }
                  hint={
                    costAvailable
                      ? 'From Meta pricing analytics'
                      : 'Not returned by Meta'
                  }
                  accent={costAvailable}
                />
                <StatTile
                  label="Delivered volume"
                  value={Number(data?.volume_total || 0).toLocaleString('en-IN')}
                  hint={hasVolume ? 'Messages delivered' : 'No volume in period'}
                />
              </div>

              {errInfo && errInfo.kind !== 'permission' ? (
                <div style={noticeStyle}>
                  <strong style={{ color: '#92400e' }}>{errInfo.title}</strong>
                  <div style={{ marginTop: 4 }}>{errInfo.detail}</div>
                </div>
              ) : (
                <div style={{ ...subStyle, marginTop: 12, maxWidth: 560 }}>
                  {data?.note ||
                    data?.hint ||
                    'Meta bills the payment method on your WhatsApp Business Account. EduOK cannot pay Meta for you.'}
                </div>
              )}

              {Array.isArray(data?.by_category) && data.by_category.length > 0 ? (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 6,
                    marginTop: 12,
                  }}
                >
                  {data.by_category.slice(0, 6).map((row) => (
                    <span key={row.category} style={chipStyle}>
                      {String(row.category || '').replace(/_/g, ' ')}
                      {': '}
                      {row.volume || 0}
                      {costAvailable && row.cost != null
                        ? ` · ${formatMoney(row.cost, currency)}`
                        : ''}
                    </span>
                  ))}
                </div>
              ) : null}
            </>
          )}
        </div>

        {!showPermissionState ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              flexShrink: 0,
            }}
          >
            <button type="button" onClick={load} disabled={loading} style={btnGhost}>
              <FaSync style={{ marginRight: 6 }} />
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
            <a href={billingUrl} target="_blank" rel="noopener noreferrer" style={btnPrimary}>
              Manage Meta billing
              <FaExternalLinkAlt size={10} style={{ marginLeft: 6 }} />
            </a>
            <a
              href={helpUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={linkHelp}
            >
              How Meta billing works
            </a>
          </div>
        ) : (
          <button type="button" onClick={load} disabled={loading} style={btnGhost}>
            <FaSync style={{ marginRight: 6 }} />
            {loading ? 'Refreshing…' : 'Try again'}
          </button>
        )}
      </div>

      <style>
        {`@keyframes waSpin { to { transform: rotate(360deg); } }`}
      </style>
    </div>
  )
}

// Created By Cursor
function IconBadge({ children }) {
  return (
    <span
      style={{
        width: 32,
        height: 32,
        borderRadius: 10,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${WA_DARK}, ${WA})`,
        color: '#fff',
        flexShrink: 0,
        boxShadow: '0 4px 10px rgba(18, 140, 126, 0.25)',
      }}
    >
      {children}
    </span>
  )
}

// Created By Cursor
function StatTile({ label, value, hint, accent }) {
  return (
    <div
      style={{
        padding: '12px 14px',
        borderRadius: 12,
        background: accent
          ? 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)'
          : '#fff',
        border: accent ? '1px solid #86efac' : '1px solid #e2e8f0',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 800,
          color: accent ? WA_DARK : '#0f172a',
          lineHeight: 1.15,
        }}
      >
        {value}
      </div>
      {hint ? (
        <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', marginTop: 4 }}>
          {hint}
        </div>
      ) : null}
    </div>
  )
}

// Created By Cursor
function PermissionCard({ title, detail, billingUrl, helpUrl }) {
  return (
    <div
      style={{
        padding: '14px 16px',
        borderRadius: 12,
        background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 45%, #fff 100%)',
        border: '1px solid #fdba74',
        maxWidth: 560,
      }}
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <span
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#fff',
            border: '1px solid #fed7aa',
            color: '#c2410c',
            flexShrink: 0,
          }}
        >
          <FaLock size={14} />
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 14, color: '#9a3412' }}>{title}</div>
          <div
            style={{
              marginTop: 6,
              fontSize: 12,
              fontWeight: 600,
              color: '#78716c',
              lineHeight: 1.5,
            }}
          >
            {detail}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
            <a href={billingUrl} target="_blank" rel="noopener noreferrer" style={btnPrimary}>
              Open Meta billing
              <FaExternalLinkAlt size={10} style={{ marginLeft: 6 }} />
            </a>
            <a href={helpUrl} target="_blank" rel="noopener noreferrer" style={btnGhost}>
              Help guide
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

// Created By Cursor
function formatMoney(amount, currency) {
  const n = Number(amount)
  if (Number.isNaN(n)) return '—'
  const cur = String(currency || 'INR').toUpperCase()
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: cur,
      maximumFractionDigits: 2,
    }).format(n)
  } catch (e) {
    return `${cur} ${n.toFixed(2)}`
  }
}

const wrapStyle = {
  margin: '0 0 14px',
  padding: '16px 18px',
  borderRadius: 14,
  border: `1px solid ${WA}55`,
  background: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 40%, #fff 100%)',
  boxShadow: '0 4px 14px rgba(15, 23, 42, 0.05)',
}

const titleStyle = {
  fontSize: 13,
  fontWeight: 800,
  color: WA_DARK,
  letterSpacing: '0.02em',
}

const subStyle = {
  fontSize: 12,
  color: '#64748b',
  fontWeight: 600,
  lineHeight: 1.45,
}

const periodPill = {
  fontSize: 11,
  fontWeight: 700,
  color: '#0f766e',
  background: '#fff',
  border: '1px solid #a7f3d0',
  borderRadius: 999,
  padding: '3px 10px',
}

const noticeStyle = {
  marginTop: 12,
  padding: '10px 12px',
  borderRadius: 10,
  background: '#fffbeb',
  border: '1px solid #fde68a',
  fontSize: 12,
  fontWeight: 600,
  color: '#78716c',
  lineHeight: 1.45,
  maxWidth: 560,
}

const chipStyle = {
  fontSize: 11,
  fontWeight: 700,
  color: '#334155',
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: 999,
  padding: '4px 9px',
}

const btnGhost = {
  border: '1px solid #cbd5e1',
  background: '#fff',
  color: '#475569',
  fontWeight: 800,
  fontSize: 12,
  padding: '8px 12px',
  borderRadius: 10,
  cursor: 'pointer',
  textAlign: 'center',
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const btnPrimary = {
  border: 'none',
  background: `linear-gradient(135deg, ${WA_DARK}, ${WA})`,
  color: '#fff',
  fontWeight: 800,
  fontSize: 12,
  padding: '8px 12px',
  borderRadius: 10,
  cursor: 'pointer',
  textAlign: 'center',
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 3px 10px rgba(37, 211, 102, 0.22)',
}

const linkHelp = {
  fontSize: 11,
  fontWeight: 700,
  color: WA_DARK,
  textAlign: 'center',
  textDecoration: 'none',
}
